import React from 'react';
import { CalendarIcon, X, Plus, Pencil, Trash2, Check, Image as ImageIcon, Mic } from 'lucide-react';
import Dropzone from '../../Dropzone';
import SpeakerAssign from '../../SpeakerAssign';
import type { EventEntry } from '../../../types/Entities';
import { listEvents, createEvent as dbCreateEvent, updateEvent as dbUpdateEvent, deleteEvent as dbDeleteEvent } from '../../../lib/events';
import { listEventTemplates, upsertEventTemplate, upsertTemplateFields } from '../../../lib/eventTemplates';
import { listContactsCore, getContactCore } from '../../../lib/contactsCore';
import { getClaimedContactIdCore } from '@gv/core';
import { useGroupContext } from '../../../lib/groups/context';
import { createTodo } from '../../../lib/todos';
import { supabase } from '../../../lib/supabase';
import { loadGoogleMapsPlaces, extractAddressParts, shortAddressLabel } from '../../../lib/googlePlaces';
import { geoErrorMessage } from '../utils/placeUtils';
import { uploadCompressedImage } from '../../../lib/imageUpload';
import { addHistory } from '../../../lib/history';
import { listEventImages, listContactImages, addContactImage } from '../../../lib/images';
import { uploadAudioToStorage, firefliesUploadFromStorage, firefliesGetStatus, waitForTranscriptId, type DiarizedSegment } from '../../../lib/transcribe';
import DatePicker from '../../DatePicker';
import MergedEntriesComposer from '../../MergedEntriesComposer';
import { TranscribeSaveEventModal as TranscribeModal } from '../../TranscribeSaveEventModal';
import TranscriptBubbles from '../../TranscriptBubbles';


interface EventsSectionProps {
    contactId: string;
    userId: string;
    onTranscriptClick?: (event: any) => void;
}

export default function EventsSection({ contactId, userId, onTranscriptClick }: EventsSectionProps) {
    const { activeGroupId } = useGroupContext();
    const [events, setEvents] = React.useState<EventEntry[]>([]);
    const [showAdd, setShowAdd] = React.useState(false);
    const [newEvt, setNewEvt] = React.useState<EventEntry>({ name: '', whoName: '', notes: '', participants: [] });
    const [showAllEvents, setShowAllEvents] = React.useState(false);
    const photoFileRef = React.useRef<HTMLInputElement | null>(null);
    // Responsive behavior: treat width < 768px as mobile for inline expand
    const [isMobile, setIsMobile] = React.useState(false);
    // Multiple photos support for Add flow
    const [newEvtPhotos, setNewEvtPhotos] = React.useState<string[]>([]);
    const [isAddPhotoPickerOpen, setIsAddPhotoPickerOpen] = React.useState(false);
    const [selectedAddPhotoIdx, setSelectedAddPhotoIdx] = React.useState<Set<number>>(new Set());
    const modalPhotoFileRef = React.useRef<HTMLInputElement | null>(null);
    const pendingOpenEventIdRef = React.useRef<string | null>(null);
    const appliedTemplateRef = React.useRef<string | null>(null);
    const appliedTemplateEditRef = React.useRef<string | null>(null);
    const [transcribeOpen, setTranscribeOpen] = React.useState(false);
    // Inline Add: Transcript mode (replaces details with upload)
    const [addTranscriptMode, setAddTranscriptMode] = React.useState(false);
    const [addTranscriptUploading, setAddTranscriptUploading] = React.useState(false);
    const [addTranscriptStatus, setAddTranscriptStatus] = React.useState<'idle' | 'uploading' | 'processing' | 'done' | 'error'>('idle');
    const [addTranscriptError, setAddTranscriptError] = React.useState<string | null>(null);
    const [addTranscriptUploadedPath, setAddTranscriptUploadedPath] = React.useState<string | null>(null);
    const [addTranscriptFile, setAddTranscriptFile] = React.useState<File | null>(null);
    const [addTranscriptSegments, setAddTranscriptSegments] = React.useState<DiarizedSegment[]>([]);
    const [addTranscriptText, setAddTranscriptText] = React.useState('');
    const [speakerLabelsMap, setSpeakerLabelsMap] = React.useState<string[]>([]);
    const [speakerNameMap, setSpeakerNameMap] = React.useState<Record<string, string>>({});
    const addTranscriptFileRef = React.useRef<HTMLInputElement | null>(null);
    // First step chooser: Call or Other
    const [addTypeChosen, setAddTypeChosen] = React.useState<'call' | 'linkedin' | 'other' | null>(null);
    // Call flow steps: 'participants' -> 'transcript' -> 'result'
    const [callStep, setCallStep] = React.useState<'participants' | 'transcript' | 'result'>('transcript');

    // Person search (who)
    const [whoQuery, setWhoQuery] = React.useState('');
    const [whoFocused, setWhoFocused] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState<Array<{ id: string; name: string; city?: string | null; state?: string | null; country?: string | null; avatar?: string | null }>>([]);

    // Location autocomplete (reuse places helpers from relationships)
    const [placeQuery, setPlaceQuery] = React.useState('');
    const [placeSug, setPlaceSug] = React.useState<Array<{ description: string; place_id: string }>>([]);
    const [placesReady, setPlacesReady] = React.useState(false);
    const [placeSession, setPlaceSession] = React.useState<any>(null);
    const [placeFetching, setPlaceFetching] = React.useState(false);
    const [placeError, setPlaceError] = React.useState<string | null>(null);
    const [placeFocused, setPlaceFocused] = React.useState(false);
    // Simple local history for "Where" choices (e.g., LinkedIn, WhatsApp)
    const [whereHistory, setWhereHistory] = React.useState<string[]>([]);
    // Per-user, per-vault key to avoid cross-user bleed in the same vault
    const getWhereKey = React.useCallback(() => {
        const gid = activeGroupId || 'global';
        const uid = userId || 'anon';
        return `gv:where_history:${gid}:${uid}`;
    }, [activeGroupId, userId]);

    // Event name suggestions
    const [nameQuery, setNameQuery] = React.useState('');
    const [nameSuggestions, setNameSuggestions] = React.useState<Array<{ name: string; notes?: string | null; last_values?: Record<string, string> | null }>>([]);
    const [nameFocused, setNameFocused] = React.useState(false);

    // Restored UX: Add details + Follow up
    const [showAddDetails, setShowAddDetails] = React.useState(false);

    // Debounced contact search for "Who" (via core)
    React.useEffect(() => {
        let active = true;
        const q = whoQuery.trim();
        if (!q) { setSuggestions([]); return; }
        setIsSearching(true);
        const t = setTimeout(async () => {
            try {
                const data = await listContactsCore(supabase as any, { query: q, limit: 6 } as any);
                if (!active) return;
                const mapped = (data || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                    city: c.city || null,
                    state: c.state || null,
                    country: c.country || null,
                    avatar: c.profile_image_url || c.photo || null,
                }));
                setSuggestions(mapped);
            } catch (e) {
                console.error('Contact search failed:', e);
                if (active) setSuggestions([]);
            } finally {
                active && setIsSearching(false);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [whoQuery]);

    // Track viewport for mobile/desktop split
    React.useEffect(() => {
        const compute = () => setIsMobile(window.innerWidth < 768);
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    // Load Google Places once for autocomplete and set readiness
    React.useEffect(() => {
        let mounted = true;
        loadGoogleMapsPlaces().then(() => mounted && setPlacesReady(true)).catch(() => mounted && setPlacesReady(false));
        return () => { mounted = false; };
    }, []);

    // Load Where history from localStorage once
    React.useEffect(() => {
        try {
            const raw = localStorage.getItem(getWhereKey());
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) setWhereHistory(arr.filter((x: any) => typeof x === 'string'));
                else setWhereHistory([]);
            } else {
                setWhereHistory([]);
            }
        } catch { setWhereHistory([]); }
    }, [getWhereKey]);

    const pushWhereHistory = React.useCallback((val?: string | null) => {
        const v = (val || '').trim();
        if (!v) return;
        setWhereHistory(prev => {
            const exists = prev.find(p => p.toLowerCase() === v.toLowerCase());
            const next = [v, ...prev.filter(p => p.toLowerCase() !== v.toLowerCase())].slice(0, 10);
            try { localStorage.setItem(getWhereKey(), JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, [getWhereKey]);

    // Debounced Places Autocomplete predictions for the Where field (disabled for Message flow)
    React.useEffect(() => {
        let active = true;
        // Disable Google predictions entirely while adding a Message
        if (addTypeChosen === 'linkedin') { setPlaceSug([]); return; }
        const q = placeQuery.trim();
        if (!placeFocused || !q || q.length < 2 || !placesReady) { setPlaceSug([]); return; }
        const t = setTimeout(() => {
            try {
                const service = new (window as any).google.maps.places.AutocompleteService();
                const req: any = { input: q, types: ['establishment', 'geocode'] };
                if (placeSession) req.sessionToken = placeSession;
                service.getPlacePredictions(req, (preds: any[]) => {
                    if (!active) return;
                    const out = (preds || []).slice(0, 6).map(p => ({ description: p.description as string, place_id: p.place_id as string }));
                    setPlaceSug(out);
                });
            } catch {
                active && setPlaceSug([]);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [placeQuery, placesReady, placeSession, placeFocused, addTypeChosen]);

    // Load templates for this user once
    React.useEffect(() => {
        let active = true;
        (async () => {
            if (!userId) return;
            try {
                const rows = await listEventTemplates(userId);
                if (!active) return;
                const mapped = rows.map(r => ({ name: r.name, notes: r.notes ?? null, last_values: (r as any).last_values || null }));
                setNameSuggestions(mapped);
            } catch { }
        })();
        return () => { active = false; };
    }, [userId]);

    // Default date to today when opening the add form
    React.useEffect(() => {
        if (!showAdd) return;
        setNewEvt(prev => ({ ...prev, when: prev.when || new Date().toISOString().slice(0, 10) }));
        setAddTypeChosen(null);
        setAddTranscriptMode(false);
        // Reset Where input state when opening Add
        try {
            setPlaceQuery('');
            setPlaceSug([]);
            setPlaceSession(null);
            setPlaceFocused(false);
            setPlaceError(null);
        } catch { /* ignore */ }
    }, [showAdd]);

    // Load existing events for this contact
    React.useEffect(() => {
        let active = true;
        (async () => {
            if (!contactId) return;
            try {
                const rows = await listEvents(contactId);
                if (!active) return;
                setEvents(rows || []);
            } catch {
                if (active) setEvents([]);
            }
        })();
        return () => { active = false; };
    }, [contactId]);

    // Listen for external refresh requests (e.g., after Add Photo flow)
    React.useEffect(() => {
        const refresh = async (e?: any) => {
            try {
                if (!contactId) return;
                const rows = await listEvents(contactId);
                setEvents(rows || []);
            } catch { /* ignore */ }
        };
        window.addEventListener('gv:refreshEvents' as any, refresh as any);
        return () => window.removeEventListener('gv:refreshEvents' as any, refresh as any);
    }, [contactId]);

    // Auto-apply template when typing an exact matching name in Add flow
    React.useEffect(() => {
        const nm = (newEvt.name || '').trim();
        if (!nm) { appliedTemplateRef.current = null; return; }
        const match = nameSuggestions.find(s => s.name.toLowerCase() === nm.toLowerCase());
        if (match && appliedTemplateRef.current !== match.name.toLowerCase()) {
            setNewEvt(prev => ({
                ...prev,
                notes: match.notes || prev.notes,
                customFields: { ...(prev as any).customFields, ...(((match as any).last_values) || {}) }
            }));
            appliedTemplateRef.current = match.name.toLowerCase();
        }
    }, [newEvt.name, nameSuggestions]);
    // Add a new event
    const addEvent = async () => {
        const name = (newEvt.name || '').trim();
        if (!name) return;
        try {
            // Determine primary photo and build entry
            const primaryPhoto = newEvtPhotos[0] || null;
            const entry: EventEntry = {
                ...newEvt,
                name,
                photoUrl: primaryPhoto,
            } as any;
            // Ensure legacy who fields are populated from participants if missing
            if ((!entry.whoName || !entry.whoContactId) && entry.participants && entry.participants.length > 0) {
                const first = entry.participants[0];
                entry.whoName = entry.whoName || first.name;
                entry.whoContactId = entry.whoContactId || first.contactId;
                entry.whoAvatar = entry.whoAvatar || first.avatar || null;
            }

            // Persist event via core-backed lib
            const saved = await dbCreateEvent(contactId, entry as any);
            setEvents(prev => [saved, ...prev]);
            // Update where history for quick reuse
            pushWhereHistory(saved.where);

            // Add any extra photos to gallery
            try {
                if (newEvtPhotos.length > 1) {
                    const extras = newEvtPhotos.slice(1);
                    for (const url of extras) {
                        try { await addContactImage(contactId, url, { source_type: 'event', source_id: saved.id }); } catch { }
                    }
                }
            } catch { }

            // Update template suggestions for this user
            try {
                await upsertEventTemplate(userId, saved.name, saved.notes || null);
                const labels = Object.keys(((saved as any).customFields) || {});
                if (labels.length > 0) await upsertTemplateFields(userId, saved.name, labels, (saved as any).customFields || {});
            } catch { }

            // Follow up handling from entries: future -> To-Do, past -> history
            try {
                const entries: Array<{ when: string; note?: string }> = Array.isArray((newEvt as any).entries) ? (newEvt as any).entries : [];
                for (const ent of entries) {
                    const dt = new Date(ent.when);
                    if (isNaN(dt.getTime())) continue;
                    const iso = dt.toISOString();
                    if (dt.getTime() > Date.now()) {
                        try {
                            let myContactId: string | null = null;
                            try { myContactId = await getClaimedContactIdCore(supabase as any, userId); } catch { }
                            const title = ((ent.note || '').trim() || `Follow up: ${saved.name}`).slice(0, 200);
                            await createTodo({ title, due_date: iso, done: false, contact_id: contactId, event_id: saved.id || null, assigned_contact_id: myContactId } as any, userId);
                        } catch { }
                    } else {
                        try {
                            await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'event_followup', details: { event_id: saved.id, when: iso, text: ent.note || '' } });
                            try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
                        } catch { }
                    }
                }
            } catch { }

            // Reset UI
            setNewEvt({ name: '', whoName: '', notes: '', participants: [] });
            setNewEvtPhotos([]);
            setShowAddDetails(false);
            setShowAdd(false);

            // Log history and notify listeners
            try {
                await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'event_added', details: { name: saved.name, when: saved.when || null } });
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
            } catch { }
        } catch (e) {
            console.error('Failed to add event', e);
        }
    };

    const removeEvent = (id: string) => setEvents(prev => prev.filter(e => e.id !== id));

    // Event modal state (view/edit)
    const [activeEvt, setActiveEvt] = React.useState<EventEntry | null>(null);
    const [activeEdit, setActiveEdit] = React.useState<EventEntry | null>(null);
    const [isEditing, setIsEditing] = React.useState(false);
    const [activePhotos, setActivePhotos] = React.useState<string[]>([]);
    const [copied, setCopied] = React.useState<string | null>(null);

    const copy = async (label: string, text?: string | null) => {
        const val = (text || '').toString();
        if (!val) return;
        try { await navigator.clipboard.writeText(val); } catch { /* ignore */ }
        setCopied(label);
        setTimeout(() => setCopied(null), 1200);
    };

    const isDirty = React.useMemo(() => {
        if (!activeEvt || !activeEdit) return false;
        const norm = (v?: string | null) => (v ?? '').trim();
        return (
            norm(activeEvt.name) !== norm(activeEdit.name) ||
            norm(activeEvt.whoName) !== norm(activeEdit.whoName) ||
            norm(activeEvt.where) !== norm(activeEdit.where) ||
            norm(activeEvt.when) !== norm(activeEdit.when) ||
            norm(activeEvt.notes) !== norm(activeEdit.notes) ||
            norm(activeEvt.photoUrl) !== norm(activeEdit.photoUrl)
        );
    }, [activeEvt, activeEdit]);

    const openEvt = (e: EventEntry) => {
        setActiveEvt(e);
        setActiveEdit({ ...e });
        setIsEditing(false);
        // Load event photos from gallery for stacked display
        (async () => {
            try {
                if (!contactId || !e.id) { setActivePhotos([]); return; }
                const imgs = await listEventImages(contactId, e.id);
                setActivePhotos(imgs.map(i => i.url));
            } catch { setActivePhotos([]); }
        })();
    };

    const closeEvt = () => { setActiveEvt(null); setActiveEdit(null); setIsEditing(false); };

    const saveEvt = async () => {
        if (!activeEvt || !activeEdit) return;
        if (!isDirty) { setIsEditing(false); return; }

        // Determine if we should create a To-Do after update (future-dated)
        let shouldEnsureTodo = false;
        try {
            const prevWhenMs = activeEvt.when ? new Date(activeEvt.when).getTime() : NaN;
            const nextWhenMs = activeEdit.when ? new Date(activeEdit.when).getTime() : NaN;
            const now = Date.now();
            if (!isNaN(nextWhenMs) && nextWhenMs > now) {
                if (isNaN(prevWhenMs) || nextWhenMs !== prevWhenMs) shouldEnsureTodo = true;
            }
        } catch { }

        try {
            // Handle any follow-up entries edited/added in modal before saving
            try {
                const entries: Array<{ when: string; note?: string }> = Array.isArray((activeEdit as any).entries) ? (activeEdit as any).entries : [];
                for (const ent of entries) {
                    const dt = new Date(ent.when);
                    if (isNaN(dt.getTime())) continue;
                    const iso = dt.toISOString();
                    if (dt.getTime() > Date.now()) {
                        try {
                            let myContactId: string | null = null;
                            try { myContactId = await getClaimedContactIdCore(supabase as any, userId); } catch { }
                            const title = ((ent.note || '').trim() || `Follow up: ${activeEdit.name || 'Follow-up'}`).slice(0, 200);
                            await createTodo({ title, due_date: iso, done: false, contact_id: contactId, event_id: activeEdit.id || null, assigned_contact_id: myContactId } as any, userId);
                        } catch { }
                    } else {
                        try {
                            await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'event_followup', details: { event_id: activeEdit.id, when: iso, text: ent.note || '' } });
                            try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
                        } catch { }
                    }
                }
                // Clear entries so they aren't persisted as part of the event
                setActiveEdit(prev => ({ ...(prev as any), entries: [] } as any));
            } catch { }

            const updated = await dbUpdateEvent(activeEvt.id!, { ...activeEdit } as any);
            setEvents(prev => prev.map(x => x.id === activeEvt.id ? updated : x));

            // Log history for update and refresh History panel
            try {
                await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'event_updated', details: { id: updated.id, name: updated.name, when: updated.when || null } });
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
            } catch { }

            // Ensure a To-Do exists if future-dated
            if (shouldEnsureTodo && updated.when) {
                try {
                    const { listTodosByEventIdsCore } = await import('@gv/core');
                    const pairs = await listTodosByEventIdsCore(supabase as any, [updated.id!]);
                    const exists = Array.isArray(pairs) && pairs.some(p => (p as any).event_id === updated.id);
                    if (!exists) {
                        const title = (updated.notes?.trim() || updated.name || 'Follow-up').slice(0, 200);
                        let myContactId: string | null = null;
                        try { myContactId = await getClaimedContactIdCore(supabase as any, userId); } catch { /* ignore */ }
                        await createTodo({ title, due_date: new Date(updated.when).toISOString(), done: false, contact_id: contactId, event_id: updated.id || null, assigned_contact_id: myContactId } as any, userId);
                    }
                } catch { /* non-blocking */ }
            }
            closeEvt();
        } catch (e) { console.error('Failed to update event', e); }
    };

    const deleteEvt = async () => {
        if (!activeEvt) return;
        try {
            // Check if this is a transcript event that should be deleted from all participants
            const isTranscript = ((activeEvt as any)?.customFields?.eventType === 'Transcribed') ||
                (activeEvt.name === 'Call Transcript') ||
                activeEvt.name?.toLowerCase().includes('call transcript') ||
                (activeEvt.notes && activeEvt.notes.split('\n').some(line => /^[^:]+:\s*/.test(line.trim())));

            if (isTranscript && activeEvt.participants && activeEvt.participants.length > 0) {
                const sharedId = (activeEvt as any)?.customFields?.sharedTranscriptId || null;
                // For transcript events, delete from all participants
                const participantIds = activeEvt.participants
                    .map((p: any) => p.contactId)
                    .filter((id: string) => id && id !== contactId); // Don't double-delete from current contact

                // Delete from current contact first
                await dbDeleteEvent(activeEvt.id!);
                removeEvent(activeEvt.id!);

                // Delete from other participants
                for (const participantId of participantIds) {
                    try {
                        // Find the same event on the participant's timeline and delete it
                        const participantEvents = await listEvents(participantId);
                        let matchingEvent: any = null;
                        if (sharedId) {
                            matchingEvent = participantEvents.find((e: any) => (e as any)?.customFields?.sharedTranscriptId === sharedId);
                        }
                        if (!matchingEvent) {
                            matchingEvent = participantEvents.find((e: any) =>
                                e.name === activeEvt.name &&
                                e.when === activeEvt.when &&
                                e.notes === activeEvt.notes
                            );
                        }
                        if (matchingEvent) {
                            await dbDeleteEvent(matchingEvent.id!);
                        }
                    } catch (e) {
                        console.error(`Failed to delete event from participant ${participantId}:`, e);
                        // Continue with other participants even if one fails
                    }
                }

                // Refresh events for all participants
                try {
                    for (const participantId of participantIds) {
                        window.dispatchEvent(new CustomEvent('gv:refreshEvents', { detail: { contactId: participantId } }));
                    }
                } catch (e) {
                    console.error('Failed to refresh participant events:', e);
                }
            } else {
                // For regular events, just delete from current contact
                await dbDeleteEvent(activeEvt.id!);
                removeEvent(activeEvt.id!);
            }

            closeEvt();
        } catch (e) {
            console.error('Failed to delete event', e);
        }
    };

    // Render saved chat bubbles using the shared TranscriptBubbles component
    function ChatViewFromEvent({ chat, event }: { chat: any; event?: any }) {
        // Render only if explicit chat segments exist; no reconstruction from notes/transcriptText
        const segments = Array.isArray(chat?.segments) ? chat.segments : [];
        const participants = Array.isArray(chat?.participants) ? chat.participants : [];
        if (!segments || segments.length === 0) return null;
        const namesSet = new Set<string>();
        for (const s of segments) { if (s?.name) namesSet.add(String(s.name)); }
        const uniqueNames = Array.from(namesSet);
        // If only one speaker, render as a simple transcript block (no chat bubbles)
        if (uniqueNames.length <= 1) {
            const mono = segments.map((s: any) => s?.text || '').filter(Boolean).join('\n');
            return (
                <div className="p-3 rounded-md bg-gray-900 border border-gray-800 text-sm text-gray-200 whitespace-pre-wrap leading-5">
                    {mono}
                </div>
            );
        }
        try { console.debug('[EventsSection] ChatViewFromEvent', { segs: segments.length, participants }); } catch { }
        const normalizedSegments = segments.map((s: any) => ({ speaker: String((s?.name ?? 'Speaker')), text: String(s?.text ?? '') }));
        const normalizedParticipants = participants.map((p: any) => ({ contactId: p?.contactId, name: String(p?.name ?? ''), avatar: p?.avatar ?? null }));
        return (
            <div className="max-w-3xl mx-auto">
                <TranscriptBubbles segments={normalizedSegments as any} participants={normalizedParticipants as any} />
            </div>
        );
    }

    return (
        <>
            <div className="bg-gray-900 rounded-xl">
                <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                    <div className="flex items-center space-x-2">
                        <CalendarIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-white">Events</h3>
                    </div>
                    {!activeEvt && (
                        <button
                            onClick={() => setShowAdd(v => !v)}
                            className="p-2 rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white"
                            aria-label={showAdd ? 'Close' : 'Add Event'}
                            title={showAdd ? 'Close' : 'Add'}
                        >
                            {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </button>
                    )}
                </div>
                <div className="px-5 pb-5 space-y-4">
                    {showAdd && (
                        <div className="rounded-lg p-4 border border-gray-700 space-y-4 bg-gray-800">
                            {addTypeChosen === null ? (
                                <div className="grid grid-cols-3 gap-3">
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            setAddTypeChosen('call');
                                            setCallStep('transcript');

                                            // Auto-populate default participants (current contact + user)
                                            try {
                                                // Get current user's contact info
                                                const myContactId = await getClaimedContactIdCore(supabase as any, userId);
                                                let myContactInfo = null;
                                                if (myContactId) {
                                                    try {
                                                        const contacts = await listContactsCore(supabase as any, { query: '', limit: 1000 });
                                                        myContactInfo = contacts?.find((c: any) => c.id === myContactId);
                                                    } catch { /* ignore */ }
                                                }

                                                // Get the target contact info  
                                                let targetContactInfo = null;
                                                try {
                                                    const contacts = await listContactsCore(supabase as any, { query: '', limit: 1000 });
                                                    targetContactInfo = contacts?.find((c: any) => c.id === contactId);
                                                } catch { /* ignore */ }

                                                const myName = myContactInfo ? `${myContactInfo.first_name || ''} ${myContactInfo.last_name || ''}`.trim() || 'Me' : 'Me';
                                                const targetName = targetContactInfo ? `${targetContactInfo.first_name || ''} ${targetContactInfo.last_name || ''}`.trim() || 'Contact' : 'Contact';

                                                // Auto-populate with default participants that can be edited/removed
                                                setNewEvt(prev => ({
                                                    ...prev,
                                                    name: (prev.name?.trim() ? prev.name : 'Call'),
                                                    participants: [
                                                        // User (from "my contact" settings)
                                                        {
                                                            contactId: myContactId || undefined,
                                                            name: myName,
                                                            avatar: myContactInfo?.profile_image_url || null
                                                        },
                                                        // Current contact being viewed
                                                        {
                                                            contactId: contactId,
                                                            name: targetName,
                                                            avatar: targetContactInfo?.profile_image_url || null
                                                        }
                                                    ]
                                                }));
                                            } catch (error) {
                                                console.error('Error setting up default participants:', error);
                                                // Fallback - just set empty participants
                                                setNewEvt(prev => ({ ...prev, name: (prev.name?.trim() ? prev.name : 'Call'), participants: [] }));
                                            }
                                        }}
                                        className="h-24 rounded-lg border border-gray-700 bg-gray-900 hover:bg-gray-700 text-white flex items-center justify-center gap-2"
                                    >
                                        <Mic className="w-4 h-4" /> Call
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            setAddTypeChosen('linkedin');
                                            // Clear Where input UI state so it never carries over
                                            setPlaceQuery('');
                                            setPlaceSug([]);
                                            setPlaceFocused(false);
                                            // Fast, optimistic defaults for Message
                                            setNewEvt(prev => ({
                                                ...prev,
                                                name: 'Message',
                                                where: '',
                                                when: new Date().toISOString().split('T')[0],
                                                participants: [],
                                                whoName: '',
                                                whoContactId: undefined,
                                                whoAvatar: null
                                            }));
                                            // Enrich participants without large list scans
                                            try {
                                                const myContactId = await getClaimedContactIdCore(supabase as any, userId);
                                                const [myContactInfo, targetContactInfo] = await Promise.all([
                                                    myContactId ? getContactCore(supabase as any, myContactId).catch(() => null) : Promise.resolve(null),
                                                    contactId ? getContactCore(supabase as any, contactId).catch(() => null) : Promise.resolve(null)
                                                ]);
                                                const myName = myContactInfo ? `${myContactInfo.first_name || ''} ${myContactInfo.last_name || ''}`.trim() || 'Me' : 'Me';
                                                const targetName = targetContactInfo ? `${targetContactInfo.first_name || ''} ${targetContactInfo.last_name || ''}`.trim() || 'Contact' : 'Contact';
                                                setNewEvt(prev => ({
                                                    ...prev,
                                                    participants: [
                                                        { contactId: myContactId || undefined, name: myName, avatar: (myContactInfo as any)?.profile_image_url || null },
                                                        { contactId, name: targetName, avatar: (targetContactInfo as any)?.profile_image_url || null }
                                                    ],
                                                    whoName: targetName,
                                                    whoContactId: contactId,
                                                    whoAvatar: (targetContactInfo as any)?.profile_image_url || null
                                                }));
                                            } catch (error) {
                                                console.error('Error setting up Message participants:', error);
                                            }
                                        }}
                                        className="h-24 rounded-lg border border-gray-700 bg-gray-900 hover:bg-gray-700 text-white flex items-center justify-center gap-2 text-sm"
                                    >
                                        💬 Message
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAddTypeChosen('other')}
                                        className="h-24 rounded-lg border border-gray-700 bg-gray-900 hover:bg-gray-700 text-white"
                                    >
                                        Other
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Event type indicator with back button */}
                                    <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-700">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-400">Creating:</span>
                                            <span className="px-2 py-1 bg-gray-800 border border-gray-600 rounded text-sm text-white">
                                                {addTypeChosen === 'call' ?
                                                    `📞 Call ${callStep === 'transcript' ? '- Upload Recording' :
                                                        callStep === 'participants' ? '- Select Participants' :
                                                            '- Review Transcript'}` :
                                                    addTypeChosen === 'linkedin' ? '💬 Message' :
                                                        '📝 Other Event'}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (addTypeChosen === 'call' && callStep !== 'transcript') {
                                                    // Go back to previous step in call flow
                                                    if (callStep === 'result') {
                                                        setCallStep('participants');
                                                    } else if (callStep === 'participants') {
                                                        setCallStep('transcript');
                                                    }
                                                } else {
                                                    // Go back to type selection
                                                    setAddTypeChosen(null);
                                                    setAddTranscriptMode(false);
                                                    setCallStep('transcript');
                                                    setNewEvt({ name: '', whoName: '', notes: '', participants: [] });
                                                    // Reset Where input state as we exit this flow
                                                    setPlaceQuery('');
                                                    setPlaceSug([]);
                                                    setPlaceSession(null);
                                                    setPlaceFocused(false);
                                                    setPlaceError(null);
                                                }
                                            }}
                                            className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700"
                                        >
                                            ← Back
                                        </button>
                                    </div>
                                    {addTypeChosen !== 'linkedin' && (
                                        <input
                                            ref={photoFileRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files || []);
                                                if (files.length === 0) return;
                                                const urls: string[] = [];
                                                for (const file of files) {
                                                    try {
                                                        const url = await uploadCompressedImage(file, `event-photos/${contactId || 'misc'}`);
                                                        urls.push(url);
                                                    } catch {
                                                        const url = URL.createObjectURL(file);
                                                        urls.push(url);
                                                    }
                                                }
                                                setNewEvtPhotos(prev => [...prev, ...urls]);
                                            }}
                                        />)}
                                    {addTypeChosen !== 'linkedin' && (
                                        <div className="w-full flex items-center justify-center">
                                            <div className="relative h-28">
                                                {newEvtPhotos[1] && (
                                                    <img
                                                        src={newEvtPhotos[1]}
                                                        alt="event alt"
                                                        className="absolute -left-8 top-1 w-24 h-24 rounded-lg object-cover rotate-[-10deg] opacity-90 border border-gray-700 z-10"
                                                    />
                                                )}
                                                {newEvtPhotos[2] && (
                                                    <img
                                                        src={newEvtPhotos[2]}
                                                        alt="event alt"
                                                        className="absolute -right-8 top-1 w-24 h-24 rounded-lg object-cover rotate-[10deg] opacity-90 border border-gray-700 z-10"
                                                    />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setIsAddPhotoPickerOpen(true)}
                                                    className="w-28 h-28 rounded-lg bg-gray-900 border border-gray-700 relative overflow-hidden z-20"
                                                >
                                                    {newEvtPhotos[0] ? (
                                                        <img src={newEvtPhotos[0]} alt="event" className="w-full h-full object-cover" />
                                                    ) : null}
                                                    {!newEvtPhotos[0] && (
                                                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                                            <div className="w-8 h-8 rounded-full bg-black/50 border border-gray-600 flex items-center justify-center">
                                                                <Plus className="w-4 h-4 text-white" />
                                                            </div>
                                                        </div>
                                                    )}
                                                    {newEvtPhotos.length > 3 && (
                                                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 border border-gray-600 text-white text-xs">
                                                            +{newEvtPhotos.length - 3}
                                                        </div>
                                                    )}
                                                </button>
                                                {newEvtPhotos.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewEvtPhotos([])}
                                                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/70 border border-gray-600 text-gray-200 hover:bg-black"
                                                        aria-label="Remove images"
                                                        title="Remove images"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>)}
                                    {addTypeChosen !== 'linkedin' && (
                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Event Name</label>
                                            <input
                                                value={newEvt.name || ''}
                                                onChange={(e) => { setNewEvt(prev => ({ ...prev, name: e.target.value })); setNameQuery(e.target.value); if (addTranscriptMode) setAddTranscriptMode(false); }}
                                                onFocus={() => setNameFocused(true)}
                                                onBlur={() => setTimeout(() => setNameFocused(false), 120)}
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                                placeholder="e.g., Family reunion, Called today"
                                            />
                                            {nameFocused && nameQuery.trim().length > 0 && (
                                                <div className="mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                                    <button
                                                        type="button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => {
                                                            setAddTranscriptMode(true);
                                                            setNewEvt(prev => ({ ...prev, name: 'Transcribed conversation', customFields: { ...(prev as any).customFields, eventType: 'Transcribed' } as any } as any));
                                                            setNameQuery('');
                                                            setNameFocused(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-700"
                                                    >
                                                        Transcribed
                                                    </button>
                                                    {nameSuggestions
                                                        .filter(s => s.name.toLowerCase().includes(nameQuery.trim().toLowerCase()))
                                                        .slice(0, 8)
                                                        .map(s => (
                                                            <button
                                                                key={s.name}
                                                                type="button"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => {
                                                                    setNewEvt(prev => ({
                                                                        ...prev,
                                                                        name: s.name,
                                                                        notes: s.notes || prev.notes,
                                                                        customFields: {
                                                                            ...(prev as any).customFields,
                                                                            ...(((s as any).last_values) || {}),
                                                                        }
                                                                    }));
                                                                    setNameQuery('');
                                                                    setNameFocused(false);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-700 last:border-b-0"
                                                            >
                                                                <div className="font-medium">{s.name}</div>
                                                                {s.notes ? (<div className="text-xs text-gray-400 line-clamp-2">{s.notes}</div>) : null}
                                                            </button>
                                                        ))}
                                                    {nameSuggestions.length === 0 && (
                                                        <div className="px-3 py-2 text-xs text-gray-500">No past names yet — suggestions will appear as you add events</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">When</label>
                                        <input
                                            type="date"
                                            value={newEvt.when || ''}
                                            onChange={(e) => setNewEvt(prev => ({ ...prev, when: e.target.value }))}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                        />
                                    </div>
                                    <div className="relative">
                                        <label className="block text-xs text-gray-400 mb-1">Where</label>
                                        <input
                                            value={placeQuery || newEvt.where || ''}
                                            onChange={(e) => { setPlaceQuery(e.target.value); setNewEvt(prev => ({ ...prev, where: e.target.value, whereMeta: e.target.value ? { provider: 'manual', label: e.target.value } as any : undefined })); }}
                                            onFocus={() => {
                                                setPlaceFocused(true);
                                                if (addTypeChosen !== 'linkedin') {
                                                    try {
                                                        const token = new (window as any).google.maps.places.AutocompleteSessionToken();
                                                        setPlaceSession(token);
                                                    } catch { setPlaceSession(null); }
                                                }
                                            }}
                                            onBlur={() => { setTimeout(() => { setPlaceFocused(false); setPlaceSug([]); setPlaceSession(null); }, 150); }}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                            placeholder={addTypeChosen === 'linkedin' ? 'Platform (e.g., LinkedIn, WhatsApp)' : 'Venue or city'}
                                        />
                                        {addTypeChosen !== 'linkedin' && (
                                            <div className="mt-1 flex items-center justify-between">
                                                <div className="text-xs text-red-300 min-h-[1rem]">{placeError || ''}</div>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        try { setPlaceError(null); } catch { }
                                                        if (!navigator.geolocation) { setPlaceError('Location not supported'); return; }
                                                        setPlaceFetching(true);
                                                        try {
                                                            const pos = await new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }));
                                                            await loadGoogleMapsPlaces();
                                                            const geocoder = new (window as any).google.maps.Geocoder();
                                                            geocoder.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any[], status: any) => {
                                                                if (status !== (window as any).google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                                                                    setPlaceError('Could not resolve address'); setPlaceFetching(false); return;
                                                                }
                                                                const best = results[0];
                                                                const parts = extractAddressParts(best.address_components);
                                                                const label = shortAddressLabel(null, parts.city || null, parts.state || null, parts.country || null) || best.formatted_address;
                                                                setNewEvt(prev => ({
                                                                    ...prev,
                                                                    where: label,
                                                                    whereMeta: { provider: 'google', place_id: best.place_id || null, lat: pos.coords.latitude, lng: pos.coords.longitude, city: parts.city || null, state: parts.state || null, country: parts.country || null, label }
                                                                }));
                                                                setPlaceQuery(''); setPlaceFocused(false); setPlaceFetching(false);
                                                            });
                                                        } catch (e) { setPlaceError('Failed to locate'); setPlaceFetching(false); }
                                                    }}
                                                    disabled={placeFetching}
                                                    className="text-xs text-blue-300 hover:text-blue-200 underline disabled:opacity-50"
                                                >
                                                    {placeFetching ? 'Locating…' : 'Use my location'}
                                                </button>
                                            </div>
                                        )}
                                        {/* Google Places suggestions (non-Message flow) */}
                                        {addTypeChosen !== 'linkedin' && placesReady && placeFocused && placeQuery.trim() && placeSug.length > 0 && (
                                            <div className="mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                                {placeSug.map(p => (
                                                    <button
                                                        key={p.place_id}
                                                        type="button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => {
                                                            try {
                                                                const svc = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
                                                                const req: any = { placeId: p.place_id, fields: ['name', 'geometry.location', 'address_components', 'formatted_address'] };
                                                                if (placeSession) req.sessionToken = placeSession;
                                                                svc.getDetails(req, (res: any, status: any) => {
                                                                    if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !res) return;
                                                                    const loc = res.geometry?.location;
                                                                    const parts = extractAddressParts(res.address_components);
                                                                    const label = shortAddressLabel(res.name || null, parts.city || null, parts.state || null, parts.country || null) || res.formatted_address || p.description;
                                                                    setNewEvt(prev => ({
                                                                        ...prev,
                                                                        where: label,
                                                                        whereMeta: { provider: 'google', place_id: p.place_id, lat: loc ? loc.lat() : null, lng: loc ? loc.lng() : null, city: parts.city || null, state: parts.state || null, country: parts.country || null, label }
                                                                    }));
                                                                    setPlaceQuery(''); setPlaceSug([]); setPlaceFocused(false);
                                                                });
                                                            } catch { /* ignore */ }
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0"
                                                    >
                                                        {p.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {/* Where history suggestions (Message flow) */}
                                        {addTypeChosen === 'linkedin' && placeFocused && whereHistory.length > 0 && (
                                            <div className="mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                                {whereHistory
                                                    .filter(w => (placeQuery || '').trim() ? w.toLowerCase().includes((placeQuery || '').trim().toLowerCase()) : true)
                                                    .map(w => (
                                                        <button
                                                            key={w}
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => {
                                                                setNewEvt(prev => ({ ...prev, where: w, whereMeta: { provider: 'manual', label: w } as any }));
                                                                setPlaceQuery('');
                                                                setPlaceFocused(false);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0"
                                                        >
                                                            {w}
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">
                                            {addTypeChosen === 'call' ? 'Participants' : 'Who'}
                                        </label>
                                        <input
                                            value={whoQuery}
                                            onChange={(e) => setWhoQuery(e.target.value)}
                                            onFocus={() => setWhoFocused(true)}
                                            onBlur={() => { setTimeout(() => setWhoFocused(false), 120); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const name = whoQuery.trim();
                                                    if (name) {
                                                        setNewEvt(prev => {
                                                            const participants = [...(prev.participants || []), { name }];
                                                            const first = participants[0] as any;
                                                            return { ...prev, participants, whoName: first.name, whoContactId: first.contactId, whoAvatar: first.avatar } as any;
                                                        });
                                                        setWhoQuery('');
                                                        setWhoFocused(false);
                                                    }
                                                }
                                            }}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                            placeholder={addTypeChosen === 'call' ? "Search and select participants, or press Enter to add typed name" : "Search and select people, or press Enter to add typed name"}
                                        />
                                        {(whoFocused || whoQuery) && suggestions.length > 0 && (
                                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                {suggestions.slice(0, 6).map(s => (
                                                    <button
                                                        key={s.id}
                                                        type="button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => {
                                                            setNewEvt(prev => {
                                                                const exists = (prev.participants || []).some((p: any) => p.contactId === s.id || p.name === s.name);
                                                                const participants = exists ? [...(prev.participants || [])] : [...(prev.participants || []), { contactId: s.id, name: s.name, avatar: s.avatar || null }];
                                                                const first = participants[0] as any;
                                                                return { ...prev, participants, whoName: first?.name, whoContactId: first?.contactId, whoAvatar: first?.avatar } as any;
                                                            });
                                                            setWhoQuery('');
                                                            setWhoFocused(false);
                                                        }}
                                                        className="flex items-center gap-2 p-2 rounded-md border border-gray-700 hover:bg-gray-700 text-left"
                                                    >
                                                        <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0">{s.avatar ? (<img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />) : (<div className="w-full h-full bg-gray-700" />)}</div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm text-gray-200 truncate">{s.name}</div>
                                                            <div className="text-xs text-gray-400 truncate">{[s.city, s.state, s.country].filter(Boolean).join(', ')}</div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {(newEvt.participants && newEvt.participants.length > 0) && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {newEvt.participants!.map((p: any, idx: number) => (
                                                    <span key={(p.contactId || p.name) + idx} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-900 border border-gray-700 text-gray-200 text-xs">
                                                        {p.avatar ? (<img src={p.avatar} alt={p.name} className="w-4 h-4 rounded-full" />) : null}
                                                        {p.name}
                                                        <button
                                                            type="button"
                                                            className="ml-1 text-gray-400 hover:text-white"
                                                            onClick={() => setNewEvt(prev => {
                                                                const arr = (prev.participants || []).filter((_: any, i: number) => i !== idx);
                                                                const first = arr[0] as any;
                                                                return { ...prev, participants: arr, whoName: first?.name, whoContactId: first?.contactId, whoAvatar: first?.avatar } as any;
                                                            })}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Call Step Indicator */}
                                    {addTypeChosen === 'call' && (
                                        <div className="mt-4 mb-4">
                                            <div className="flex items-center justify-center space-x-4">
                                                {/* Step 1: Upload */}
                                                <div className="flex items-center">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${callStep === 'transcript' ? 'bg-blue-600 text-white' :
                                                        callStep === 'participants' || callStep === 'result' ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                                                        }`}>
                                                        {callStep === 'participants' || callStep === 'result' ? '✓' : '1'}
                                                    </div>
                                                    <span className={`ml-2 text-sm ${callStep === 'transcript' ? 'text-blue-400' : 'text-gray-400'}`}>
                                                        Upload
                                                    </span>
                                                </div>

                                                {/* Arrow */}
                                                <div className="text-gray-500">→</div>

                                                {/* Step 2: Participants */}
                                                <div className="flex items-center">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${callStep === 'participants' ? 'bg-blue-600 text-white' :
                                                        callStep === 'result' ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                                                        }`}>
                                                        {callStep === 'result' ? '✓' : '2'}
                                                    </div>
                                                    <span className={`ml-2 text-sm ${callStep === 'participants' ? 'text-blue-400' : 'text-gray-400'}`}>
                                                        Participants
                                                    </span>
                                                </div>

                                                {/* Arrow */}
                                                <div className="text-gray-500">→</div>

                                                {/* Step 3: Review */}
                                                <div className="flex items-center">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${callStep === 'result' ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                                                        }`}>
                                                        3
                                                    </div>
                                                    <span className={`ml-2 text-sm ${callStep === 'result' ? 'text-blue-400' : 'text-gray-400'}`}>
                                                        Review
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}



                                    {!addTranscriptMode && (addTypeChosen !== 'call' || callStep === 'participants') ? (
                                        <div>
                                            {addTypeChosen === 'linkedin' ? (
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Message</label>
                                                    <textarea
                                                        value={newEvt.notes || ''}
                                                        onChange={(e) => setNewEvt(prev => ({ ...prev, notes: e.target.value }))}
                                                        placeholder="Add message"
                                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                                        rows={4}
                                                    />
                                                </div>
                                            ) : (
                                                !showAddDetails ? (
                                                    <button type="button" onClick={() => setShowAddDetails(true)} className="w-full text-center py-2 rounded-md border border-gray-700 hover:border-gray-600 hover:bg-gray-800/40 text-gray-300">
                                                        <span className="inline-flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add details</span>
                                                    </button>
                                                ) : (
                                                    <div>
                                                        <div className="flex items-center justify-between">
                                                            <label className="block text-xs text-gray-400 mb-1">Details</label>
                                                            <button type="button" onClick={() => setShowAddDetails(false)} className="text-xs text-gray-400 hover:text-gray-200">Hide</button>
                                                        </div>
                                                        <textarea
                                                            value={newEvt.notes || ''}
                                                            onChange={(e) => setNewEvt(prev => ({ ...prev, notes: e.target.value }))}
                                                            placeholder="Add details"
                                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                                            rows={4}
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : (addTranscriptMode || (addTypeChosen === 'call' && callStep === 'transcript')) ? (
                                        <div className="rounded-md border border-gray-700 p-4 bg-gray-900">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-sm font-medium text-gray-200">
                                                    {addTypeChosen === 'call' ? 'Upload Call Recording' : 'Upload audio to transcribe'}
                                                </div>
                                                {addTypeChosen === 'call' && (
                                                    <div className="text-xs text-gray-400">
                                                        Step 2 of 3
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-3">
                                                {addTranscriptFile && (
                                                    <div className="w-full flex items-center justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                if (!addTranscriptFile) return;
                                                                setAddTranscriptUploading(true); setAddTranscriptStatus('uploading'); setAddTranscriptError(null);
                                                                try {
                                                                    const { path } = await uploadAudioToStorage(addTranscriptFile, 'transcripts');
                                                                    setAddTranscriptUploadedPath(path);
                                                                    setAddTranscriptStatus('processing');
                                                                    const title = addTranscriptFile.name.replace(/\.[^.]+$/, '');
                                                                    const attendees = (newEvt.participants || []).map((p: any) => ({ name: p.name }));
                                                                    const up = await firefliesUploadFromStorage({ storagePath: path, title, attendees, userId });
                                                                    let transcriptId: string | null = null;
                                                                    try { transcriptId = await waitForTranscriptId((up as any).client_reference_id); } catch { /* proceed to poll */ }
                                                                    const id = transcriptId || (up as any).transcript_id || (up as any).meeting_id;
                                                                    if (!id) throw new Error('Could not start transcription');
                                                                    let done = false; let tries = 0; let status: any = null;
                                                                    while (!done && tries < 80) {
                                                                        await new Promise(r => setTimeout(r, 4000));
                                                                        tries++;
                                                                        status = await firefliesGetStatus(id);
                                                                        done = (status?.status && /completed|done|ready/i.test(status.status)) || (status?.segments?.length > 0) || (status?.text?.length > 0);
                                                                    }
                                                                    if (!done) throw new Error('Transcription timed out');
                                                                    const segments: DiarizedSegment[] = status?.segments || [];
                                                                    const text: string = status?.text || '';
                                                                    setAddTranscriptSegments(segments);
                                                                    setAddTranscriptText(text);
                                                                    const labels: string[] = [];
                                                                    const seen = new Set<string>();
                                                                    for (const s of (segments || [])) { const lbl = (s.speaker || '').trim() || 'Speaker'; if (!seen.has(lbl)) { seen.add(lbl); labels.push(lbl); } }
                                                                    const initNameMap: Record<string, string> = {};
                                                                    labels.forEach((lbl, i) => { initNameMap[lbl] = labels.length > 1 ? `Speaker ${i + 1}` : (lbl || `Speaker ${i + 1}`); });
                                                                    setSpeakerLabelsMap(labels);
                                                                    setSpeakerNameMap(initNameMap);
                                                                    const chat = {
                                                                        primary: (newEvt.participants && (newEvt.participants as any[])[0]?.name) || null,
                                                                        participants: (newEvt.participants || []),
                                                                        segments: (segments || []).map((s: any) => ({ name: initNameMap[s.speaker] || s.speaker || 'Speaker', text: s.text || '' }))
                                                                    };
                                                                    setNewEvt(prev => ({
                                                                        ...prev,
                                                                        customFields: { ...(prev as any).customFields, eventType: 'Transcribed', chat, transcriptText: text },
                                                                    } as any));
                                                                    try { if (addTranscriptUploadedPath) { await supabase.storage.from('audio').remove([addTranscriptUploadedPath]); } } catch { }
                                                                    setAddTranscriptUploadedPath(null);
                                                                    setAddTranscriptStatus('done');
                                                                } catch (err: any) {
                                                                    setAddTranscriptError(err?.message || 'Failed to transcribe');
                                                                    setAddTranscriptStatus('error');
                                                                } finally {
                                                                    setAddTranscriptUploading(false);
                                                                }
                                                            }}
                                                            className="px-4 py-2 text-sm rounded-md border border-blue-700 bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2"
                                                            disabled={!addTranscriptFile || addTranscriptUploading || addTranscriptStatus === 'processing'}
                                                        >
                                                            {(addTranscriptUploading || addTranscriptStatus === 'processing') && (
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {addTranscriptStatus === 'uploading' && 'Uploading...'}
                                                            {addTranscriptStatus === 'processing' && 'Processing...'}
                                                            {addTranscriptStatus !== 'uploading' && addTranscriptStatus !== 'processing' && 'Transcribe'}
                                                        </button>
                                                    </div>
                                                )}
                                                <Dropzone accept="audio/*" file={addTranscriptFile} onFileSelected={(f) => {
                                                    setAddTranscriptFile(f);
                                                    setAddTranscriptError(null);
                                                    setAddTranscriptStatus('idle');
                                                    setAddTranscriptSegments([]);
                                                    setAddTranscriptText('');
                                                    setSpeakerLabelsMap([]);
                                                    setSpeakerNameMap({});
                                                }} label="Drag & drop audio here, or click to select" disabled={addTranscriptUploading || addTranscriptStatus === 'processing'} />
                                                {addTranscriptError && <div className="text-xs text-red-300">{addTranscriptError}</div>}
                                                {addTranscriptStatus === 'done' && <div className="text-xs text-green-300">Transcript added to this event</div>}

                                                {/* Call Step Navigation */}
                                                {addTypeChosen === 'call' && (
                                                    <div className="mt-4 flex justify-between">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setAddTypeChosen(null);
                                                                setCallStep('transcript');
                                                            }}
                                                            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-md"
                                                        >
                                                            ← Back to Event Types
                                                        </button>
                                                        {addTranscriptStatus === 'done' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setCallStep('participants')}
                                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                                                            >
                                                                Next: Select Participants →
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {addTranscriptStatus === 'done' && speakerLabelsMap.length > 0 && (
                                                <div className="mt-3 rounded-md border border-gray-700 p-3 bg-gray-900">
                                                    <SpeakerAssign
                                                        speakerLabels={speakerLabelsMap}
                                                        value={Object.fromEntries(speakerLabelsMap.map((lbl, i) => [lbl, { contactId: null, displayName: speakerNameMap[lbl] || `Speaker ${i + 1}` }]))}
                                                        onChange={(map) => {
                                                            const nm: Record<string, string> = {}
                                                            for (const [k, v] of Object.entries(map)) nm[k] = v.displayName
                                                            setSpeakerNameMap(nm)
                                                            setNewEvt(prev => {
                                                                const chat = (((prev as any).customFields || {}).chat) || { segments: [] };
                                                                const segs = (addTranscriptSegments || []).map(s => ({ name: (nm[s.speaker] || s.speaker || 'Speaker'), text: s.text || '' }));
                                                                const updated = { ...prev, customFields: { ...(prev as any).customFields, eventType: 'Transcribed', chat: { ...(chat as any), primary: (prev.participants && (prev.participants as any[])[0]?.name) || null, participants: (prev.participants || []), segments: segs }, transcriptText: addTranscriptText } } as any;
                                                                return updated;
                                                            });
                                                        }}
                                                    />
                                                    <div className="mt-2 text-[11px] text-gray-500">These names will be used in the chat-style transcript and matched to participant avatars.</div>
                                                </div>
                                            )}
                                        </div>
                                    ) : null}

                                    {/* Call Step: Result/Review */}
                                    {addTypeChosen === 'call' && callStep === 'result' && addTranscriptText && (
                                        <div className="mt-4 space-y-4">
                                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="text-sm font-medium text-gray-200">Call Transcript Ready</h4>
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-flex items-center px-2 py-1 text-xs rounded-md border border-green-700 bg-green-900/20 text-green-200">
                                                            ✓ Processed
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Beautiful Transcript Preview */}
                                                <div className="bg-gray-800 border border-gray-700 rounded-md p-4 max-h-64 overflow-y-auto">
                                                    <div className="space-y-3 text-sm">
                                                        {addTranscriptSegments.map((segment, idx) => {
                                                            const speakerName = speakerNameMap[segment.speaker] || segment.speaker;
                                                            return (
                                                                <div key={idx} className="flex gap-3">
                                                                    <div className="flex-shrink-0 w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-xs font-medium text-gray-300">
                                                                        {speakerName.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className="text-xs font-medium text-gray-400">{speakerName}</span>
                                                                        </div>
                                                                        <div className="text-gray-200 leading-relaxed">{segment.text}</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="mt-4 flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(addTranscriptText);
                                                            showToast('Transcript copied to clipboard!', 'success');
                                                        }}
                                                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md flex items-center gap-2"
                                                    >
                                                        📋 Copy Transcript
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const talkingPoints = generateTalkingPoints({ text: addTranscriptText });
                                                            setNewEvt(prev => ({ ...prev, talkingPoints }));
                                                            showToast('Talking points generated!', 'success');
                                                        }}
                                                        className="px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs rounded-md flex items-center gap-2"
                                                    >
                                                        💡 Generate Talking Points
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Navigation */}
                                            <div className="flex justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => setCallStep('participants')}
                                                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-md"
                                                >
                                                    ← Back to Participants
                                                </button>
                                                <div className="text-xs text-gray-400 flex items-center">
                                                    Ready to save this call event
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <MergedEntriesComposer newEvt={newEvt as any} setNewEvt={setNewEvt as any} addButtonLabel="Add Follow up" fullWidthAddButton />

                                    {/* Call Step: Participants Navigation */}
                                    {addTypeChosen === 'call' && callStep === 'participants' && (
                                        <div className="mt-4 flex justify-between">
                                            <button
                                                type="button"
                                                onClick={() => setCallStep('transcript')}
                                                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-md"
                                            >
                                                ← Back to Upload
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCallStep('result')}
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                                            >
                                                Next: Review Call →
                                            </button>
                                        </div>
                                    )}

                                    {(newEvt.when && new Date(newEvt.when) > new Date()) && (
                                        <div className="mt-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="text-xs text-gray-400">Reminders for this event</label>
                                            </div>
                                            <div className="bg-gray-900 border border-gray-700 rounded-md p-3 text-xs text-gray-300">
                                                This event is scheduled in the future, so it will appear in Follow ups. Optionally add multiple reminder dates after saving from the event details.
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAdd(false);
                                                setNewEvt({ name: '', whoName: '', notes: '', tasks: [], participants: [] });
                                                setAddTypeChosen(null);
                                                setCallStep('transcript');
                                                setAddTranscriptMode(false);
                                                setAddTranscriptFile(null);
                                                setAddTranscriptStatus('idle');
                                                setAddTranscriptError(null);
                                                setAddTranscriptSegments([]);
                                                setAddTranscriptText('');
                                                // Also clear Where input state so the next Add starts fresh
                                                setPlaceQuery('');
                                                setPlaceSug([]);
                                                setPlaceSession(null);
                                                setPlaceFocused(false);
                                                setPlaceError(null);
                                            }}
                                            className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                // Set default name for calls if not set
                                                if (addTypeChosen === 'call' && !newEvt.name?.trim()) {
                                                    const participantNames = (newEvt.participants || []).map((p: any) => p.name).join(', ');
                                                    const defaultName = participantNames ? `Call with ${participantNames}` : 'Phone Call';
                                                    setNewEvt(prev => ({ ...prev, name: defaultName }));
                                                }
                                                addEvent();
                                            }}
                                            disabled={
                                                (addTypeChosen === 'call' && callStep !== 'result')
                                            }
                                            className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                                        >
                                            {addTypeChosen === 'call' && callStep !== 'result' ? 'Complete Call Steps First' : 'Create Event'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {/* Existing Events List */}
                    {events.length > 0 ? (
                        <div className="space-y-3">
                            {(showAllEvents ? events : events.slice(0, 1)).map(e => (
                                <div key={e.id} className="w-full">
                                    <button
                                        data-event-id={e.id}
                                        aria-selected={activeEvt?.id === e.id ? true : undefined}
                                        onClick={() => {
                                            if (activeEvt?.id === e.id) {
                                                closeEvt();
                                            } else {
                                                // Check if this is a transcript event
                                                const isTranscript = ((e as any)?.customFields?.eventType === 'Transcribed') ||
                                                    e.name?.toLowerCase().includes('transcript') ||
                                                    e.name?.toLowerCase().includes('call transcript') ||
                                                    (e.notes && e.notes.includes(':')) && // Contains speaker format like "Name: text"
                                                    (e.notes.split('\n').some(line => /^[^:]+:\s*/.test(line.trim())));

                                                if (isTranscript && onTranscriptClick) {
                                                    // Convert event to the format expected by TranscriptView, preserving saved participants/customFields
                                                    const transcriptEvent = {
                                                        id: e.id,
                                                        title: e.name,
                                                        notes: e.notes || '',
                                                        description: e.notes || '',
                                                        due_date: e.when,
                                                        participants: (e as any).participants,
                                                        customFields: (e as any).customFields,
                                                    } as any;
                                                    onTranscriptClick(transcriptEvent);
                                                } else {
                                                    openEvt(e);
                                                }
                                            }
                                        }}
                                        className={`w-full text-left bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-4 border ${activeEvt?.id === e.id ? 'border-gray-600' : 'border-gray-700'} hover:border-gray-600 transition-colors cursor-pointer`}
                                    >
                                        <div className="flex items-center gap-4 min-w-0">
                                            {/* Thumbnail area with placeholder fallback */}
                                            <div className="w-14 h-14 rounded-lg bg-gray-700 overflow-hidden flex-shrink-0 border border-gray-600">
                                                <img
                                                    src={
                                                        e.photoUrl ||
                                                        (((e as any)?.customFields?.eventType === 'Transcribed')
                                                            ? '/icons/meeting-default.svg'
                                                            : ((e.name && /call|phone/i.test(e.name)) ? '/icons/phone-default.svg' : '/icons/meeting-default.svg'))
                                                    }
                                                    alt="event"
                                                    className={`w-full h-full ${e.photoUrl ? 'object-cover' : 'object-contain p-2'} bg-gray-800`}
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="text-white font-medium truncate flex items-center gap-2">
                                                        {((e as any)?.customFields?.eventType === 'Transcribed') && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-md border border-blue-700 bg-blue-900/20 text-blue-200">Transcribed</span>
                                                        )}
                                                        <span className="truncate">{e.name}</span>
                                                    </div>
                                                    {e.when ? (
                                                        <div className="text-xs text-gray-400 whitespace-nowrap">{new Date(e.when).toLocaleDateString()}</div>
                                                    ) : null}
                                                </div>
                                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                                                    {e.where ? <span className="truncate">{e.where}</span> : null}
                                                    {/* Show all participants for transcripts, fallback to single whoName for regular events */}
                                                    {((e as any)?.participants && (e as any).participants.length > 0) ? (
                                                        <span className="truncate">• With: {(e as any).participants.map((p: any) => p.name).join(', ')}</span>
                                                    ) : e.whoName ? (
                                                        <span className="truncate">• {e.whoName}</span>
                                                    ) : null}
                                                </div>
                                                {e.notes ? (
                                                    <div className="mt-1 text-[13px] text-gray-300 line-clamp-2">{e.notes}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </button>
                                    {/* Inline expanded details on mobile */}
                                    {(isMobile && activeEvt?.id === e.id && activeEdit) ? (
                                        <div className="mt-2 bg-gray-900 border border-gray-700 rounded-lg p-4">
                                            {/* Top row with title (left-aligned) and actions */}
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <input
                                                        ref={modalPhotoFileRef}
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={async (e) => {
                                                            if (!isEditing) return;
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            try {
                                                                const url = await uploadCompressedImage(file, `event-photos/${contactId || 'misc'}`);
                                                                setActiveEdit(prev => ({ ...(prev as any), photoUrl: url }));
                                                            } catch {
                                                                const url = URL.createObjectURL(file);
                                                                setActiveEdit(prev => ({ ...(prev as any), photoUrl: url }));
                                                            }
                                                        }}
                                                    />
                                                    {/* Always show an image area; click to change only when editing */}
                                                    {isEditing ? (
                                                        <button type="button" onClick={() => modalPhotoFileRef.current?.click()} className="w-12 h-12 rounded-md bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-600">
                                                            <img
                                                                src={activeEdit.photoUrl || (((activeEdit as any)?.customFields?.eventType === 'Transcribed') ? '/icons/meeting-default.svg' : ((activeEdit.name && /call|phone/i.test(activeEdit.name)) ? '/icons/phone-default.svg' : '/icons/meeting-default.svg'))}
                                                                alt={activeEdit.name}
                                                                className={`${activeEdit.photoUrl ? 'object-cover' : 'object-contain p-2'} w-full h-full`}
                                                            />
                                                        </button>
                                                    ) : (
                                                        <div className="w-12 h-12 rounded-md bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-600">
                                                            <img
                                                                src={activeEdit.photoUrl || (((activeEdit as any)?.customFields?.eventType === 'Transcribed') ? '/icons/meeting-default.svg' : ((activeEdit.name && /call|phone/i.test(activeEdit.name)) ? '/icons/phone-default.svg' : '/icons/meeting-default.svg'))}
                                                                alt={activeEdit.name}
                                                                className={`${activeEdit.photoUrl ? 'object-cover' : 'object-contain p-2'} w-full h-full`}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className={`min-w-0 text-white ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600 rounded px-1' : ''}`} onClick={() => { if (!isEditing && activeEdit.name) copy('name', activeEdit.name); }} title={!isEditing ? 'Click to copy' : undefined}>
                                                        <div className="text-base font-medium truncate">{activeEdit.name || '\u2014'}</div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!isEditing ? (
                                                        <button aria-label="Edit" title="Edit" onClick={() => setIsEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {isDirty && (
                                                                <button onClick={saveEvt} className="px-2.5 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save changes" aria-label="Save changes">
                                                                    Save
                                                                </button>
                                                            )}
                                                            <button onClick={deleteEvt} className="p-2 rounded-md border border-red-700 bg-red-900/30 text-red-200 hover:bg-red-900/40" title="Delete" aria-label="Delete">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            {!isDirty && (
                                                                <button onClick={() => setIsEditing(false)} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Done" aria-label="Done">
                                                                    <Check className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    <button onClick={closeEvt} aria-label="Close" title="Close" className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Details form */}
                                            <div className="mt-4 space-y-3">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Name</label>
                                                    <input readOnly={!isEditing} value={activeEdit.name || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), name: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.name) copy('name', activeEdit.name); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">When</label>
                                                    <input type="date" readOnly={!isEditing} value={activeEdit.when || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), when: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.when) copy('when', activeEdit.when); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Where</label>
                                                    <input readOnly={!isEditing} value={activeEdit.where || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), where: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.where) copy('where', activeEdit.where); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                                                </div>
                                                {/* Show participants if they exist (for transcripts and multi-person events) */}
                                                {((activeEdit as any)?.participants && (activeEdit as any).participants.length > 0) && (
                                                    <div>
                                                        <label className="block text-xs text-gray-400 mb-1">Participants</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {(activeEdit as any).participants.map((p: any, idx: number) => (
                                                                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md">
                                                                    {p.avatar && <img src={p.avatar} alt={p.name} className="w-5 h-5 rounded-full" />}
                                                                    <span className="text-sm text-gray-200">{p.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Notes</label>
                                                    <textarea readOnly={!isEditing} value={activeEdit.notes || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), notes: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.notes) copy('notes', activeEdit.notes); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} rows={4} />
                                                </div>
                                                {/** Saved transcript chat, rendered exactly as saved */}
                                                {((activeEvt as any)?.customFields?.chat?.segments?.length || 0) > 0 && (
                                                    <div>
                                                        <label className="block text-xs text-gray-400 mb-1">Transcript</label>
                                                        <div className="max-h-60 overflow-auto rounded-md bg-gray-900 border border-gray-800 p-2">
                                                            <ChatViewFromEvent chat={(activeEvt as any).customFields.chat} />
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Follow up parity */}
                                                <MergedEntriesComposer newEvt={activeEdit as any} setNewEvt={setActiveEdit as any} addButtonLabel="Add Follow up" fullWidthAddButton />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                            {events.length > 1 && (
                                <div className="pt-1 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowAllEvents(v => !v)}
                                        className="px-3 py-1.5 text-xs rounded-md border border-transparent bg-gray-900/70 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-700 active:border-gray-700 focus-visible:border-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-600 transition-colors"
                                    >
                                        {showAllEvents ? 'View less' : `View ${events.length - 1} more`}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (!showAdd && (
                        <div className="rounded-lg p-4 border border-dashed border-gray-700 bg-transparent">
                            <p className="text-sm text-gray-500">No events yet. Add one to get started.</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Event modal (desktop only) */}
            {activeEvt && activeEdit && !isMobile && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={closeEvt} />
                    <div className="relative w-full h-full sm:h-auto sm:max-w-lg bg-gray-900 border border-gray-700 rounded-none sm:rounded-xl p-5 shadow-2xl overflow-auto">
                        {/* Top-left: Delete while editing */}
                        {isEditing && (
                            <div className="absolute top-3 left-3 z-50">
                                <button onClick={deleteEvt} className="p-2 rounded-md border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-900/70 inline-flex items-center gap-2 shadow-lg" title="Delete" aria-label="Delete">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {/* Top-right: Edit / Check / Save */}
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                            {!isEditing ? (
                                <button aria-label="Edit" title="Edit" onClick={() => setIsEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                    <Pencil className="w-4 h-4" />
                                </button>
                            ) : (
                                <>
                                    {isDirty ? (
                                        <button onClick={saveEvt} className="px-2.5 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save changes" aria-label="Save changes">
                                            Save
                                        </button>
                                    ) : (
                                        <button onClick={() => setIsEditing(false)} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Done" aria-label="Done">
                                            <Check className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={deleteEvt}
                                        className="px-3 py-1.5 text-sm rounded-md border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-900/70"
                                        title="Delete"
                                        aria-label="Delete"
                                    >
                                        Delete
                                    </button>
                                </>
                            )}
                            <button onClick={closeEvt} aria-label="Close" title="Close" className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Header: image on top, name below (left-aligned) */}
                        <div className="flex flex-col items-start text-left">
                            <input
                                ref={modalPhotoFileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    if (!isEditing) return;
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    try {
                                        const url = await uploadCompressedImage(file, `event-photos/${contactId || 'misc'}`);
                                        setActiveEdit(prev => ({ ...(prev as any), photoUrl: url }));
                                    } catch {
                                        const url = URL.createObjectURL(file);
                                        setActiveEdit(prev => ({ ...(prev as any), photoUrl: url }));
                                    }
                                }}
                            />
                            {/* Always show an image area; click to change only when editing */}
                            {isEditing ? (
                                <button type="button" onClick={() => modalPhotoFileRef.current?.click()} className="w-16 h-16 rounded-md bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-600">
                                    <img
                                        src={activeEdit.photoUrl || (((activeEdit as any)?.customFields?.eventType === 'Transcribed') ? '/icons/meeting-default.svg' : ((activeEdit.name && /call|phone/i.test(activeEdit.name)) ? '/icons/phone-default.svg' : '/icons/meeting-default.svg'))}
                                        alt={activeEdit.name}
                                        className={`${activeEdit.photoUrl ? 'object-cover' : 'object-contain p-2'} w-full h-full`}
                                    />
                                </button>
                            ) : (
                                <div className="w-16 h-16 rounded-md bg-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-600">
                                    <img
                                        src={activeEdit.photoUrl || (((activeEdit as any)?.customFields?.eventType === 'Transcribed') ? '/icons/meeting-default.svg' : ((activeEdit.name && /call|phone/i.test(activeEdit.name)) ? '/icons/phone-default.svg' : '/icons/meeting-default.svg'))}
                                        alt={activeEdit.name}
                                        className={`${activeEdit.photoUrl ? 'object-cover' : 'object-contain p-2'} w-full h-full`}
                                    />
                                </div>
                            )}
                            <div className={`mt-2 truncate max-w-[80%] text-white ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600 rounded px-1' : ''}`} onClick={() => { if (!isEditing && activeEdit.name) copy('name', activeEdit.name); }} title={!isEditing ? 'Click to copy' : undefined}>
                                <span className="text-base font-medium">{activeEdit.name || '\u2014'}</span>
                            </div>
                        </div>

                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Name</label>
                                <input readOnly={!isEditing} value={activeEdit.name || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), name: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.name) copy('name', activeEdit.name); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">When</label>
                                <input type="date" readOnly={!isEditing} value={activeEdit.when || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), when: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.when) copy('when', activeEdit.when); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Where</label>
                                <input readOnly={!isEditing} value={activeEdit.where || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), where: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.where) copy('where', activeEdit.where); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} />
                            </div>
                            {/* Show participants if they exist (for transcripts and multi-person events) */}
                            {((activeEdit as any)?.participants && (activeEdit as any).participants.length > 0) && (
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Participants</label>
                                    <div className="flex flex-wrap gap-2">
                                        {(activeEdit as any).participants.map((p: any, idx: number) => (
                                            <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md">
                                                {p.avatar && <img src={p.avatar} alt={p.name} className="w-5 h-5 rounded-full" />}
                                                <span className="text-sm text-gray-200">{p.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Notes</label>
                                <textarea readOnly={!isEditing} value={activeEdit.notes || ''} onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), notes: e.target.value }))} onClick={() => { if (!isEditing && activeEdit.notes) copy('notes', activeEdit.notes); }} className={`w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 ${!isEditing ? 'cursor-copy hover:ring-1 hover:ring-gray-600' : ''}`} rows={4} />
                            </div>
                            {/** Saved transcript chat, rendered exactly as saved */}
                            {((activeEvt as any)?.customFields?.chat?.segments?.length || 0) > 0 && (
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Transcript</label>
                                    <div className="max-h-60 overflow-auto rounded-md bg-gray-900 border border-gray-800 p-2">
                                        <ChatViewFromEvent chat={(activeEvt as any).customFields.chat} />
                                    </div>
                                </div>
                            )}
                            {/* Follow up parity: allow adding entries inline while editing */}
                            <MergedEntriesComposer newEvt={activeEdit as any} setNewEvt={setActiveEdit as any} addButtonLabel="Add Follow up" fullWidthAddButton />
                            {/* Bottom row removed; actions live in header */}
                        </div>
                    </div>
                </div>
            )}
            {/* Transcribe Modal for this contact */}
            <TranscribeModal userId={userId} open={transcribeOpen} onClose={() => setTranscribeOpen(false)} />
        </>
    );
}